import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The video element.
 * @property {HTMLSourceElement[]} sources - The source elements.
 */

/**
 * A lazy-loading video component that only loads the video when it enters the viewport.
 * Uses IntersectionObserver for efficient viewport detection.
 *
 * @extends Component<Refs>
 */
class LazyVideoComponent extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setupObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#destroyObserver();
  }

  /**
   * Sets up the IntersectionObserver to detect when the component enters the viewport.
   */
  #setupObserver() {
    if (this.#loaded) return;

    const video = this.refs.video;
    if (!video) return;

    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    };

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.#loaded) {
          this.#loadVideo();
          this.#destroyObserver();
        }
      }
    }, options);

    this.#observer.observe(this);
  }

  /**
   * Destroys the IntersectionObserver.
   */
  #destroyObserver() {
    if (this.#observer) {
      this.#observer.disconnect();
      this.#observer = null;
    }
  }

  /**
   * Loads and plays the video by setting the src attribute from data-src.
   */
  #loadVideo() {
    const video = this.refs.video;
    if (!video || this.#loaded) return;

    this.#loaded = true;

    const sources = Array.isArray(this.refs.sources) ? this.refs.sources : [];

    if (sources.length > 0) {
      for (const source of sources) {
        const dataSrc = source.getAttribute('data-src');
        if (dataSrc) {
          source.setAttribute('src', dataSrc);
        }
      }
    }

    video.load();

    video.addEventListener('canplay', () => {
      this.classList.add('lazy-video--loaded');
      video.play().catch(() => { });
    }, { once: true });
  }

  /**
   * Called when the element is re-rendered by the Section Rendering API.
   */
  updatedCallback() {
    super.updatedCallback();

    if (!this.#loaded) {
      this.#setupObserver();
    }
  }
}

if (!customElements.get('lazy-video')) {
  customElements.define('lazy-video', LazyVideoComponent);
}
